import { useState } from "react";
import { ChevronDown, ChevronRight, Flame } from "lucide-react";

const FuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const efficiencyMetrics = [
    {
      id: 1,
      title: "Total MT/Day",
      definition: "Total fuel consumption in metric tons per day across the entire fleet",
      formula: "Total MT/Day = Σ(All Vessel Fuel Consumption) / Number of Days",
      calculationDetails: [
        "Aggregates fuel consumption from all vessels in selected fleet(s)",
        "Includes HFO, LFO, MGO, and other fuel types",
        "Daily average calculated over selected time period",
        "Statistical ranges (min/max) show variability across vessels",
        "Target value: Typically 20.2 MT/day (configurable per fleet)"
      ],
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: 2,
      title: "Vessel Fuel Parameters",
      definition: "Individual vessel parameters stored in VesselFuelParameter collection",
      formula: "Vessel Record = { recordDate, parameters: { 'Total MT/Day': value } }",
      calculationDetails: [
        "Each vessel submits daily fuel parameter data",
        "Parameters object contains various fuel-related metrics",
        "Currently focuses on 'Total MT/Day' as primary metric",
        "Data sorted chronologically for trend analysis",
        "Missing dates are filled with zeros for continuity"
      ],
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: 3,
      title: "Fleet Target Calculation",
      definition: "How fleet efficiency targets are derived from individual vessel targets",
      formula: "Fleet Target = Average of Vessel-Specific Fuel Efficiency Targets",
      calculationDetails: [
        "Each vessel may have individual fuel efficiency targets",
        "Fleet target calculated as average of all vessel targets",
        "Defaults to 20.2 MT/day if no vessel targets specified",
        "Target comparison shows fleet performance vs. goals",
        "Helps identify vessels needing efficiency improvements"
      ],
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e9cbf5] rounded-lg">
              <Flame className="w-6 h-6 text-[#7d3c98]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fuel Efficiency Metrics
              </h2>
              <p className="text-gray-600 text-sm">
                Fleet fuel consumption analysis and aggregation methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
              <p className="text-sm text-purple-800">
                <strong>Primary Metric:</strong> The system currently focuses on <strong>"Total MT/Day"</strong> as the main fuel efficiency metric, calculated as daily fleet-wide fuel consumption averages with statistical ranges.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {efficiencyMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`p-5 rounded-lg border ${metric.borderColor} ${metric.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {metric.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Definition:</p>
                    <p className={`text-sm ${metric.textColor} italic`}>
                      {metric.definition}
                    </p>
                  </div>

                  <div className="mb-3 p-2 bg-white rounded border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-1">Formula/Method:</p>
                    <p className={`text-sm font-semibold ${metric.textColor}`}>
                      {metric.formula}
                    </p>
                  </div>

                  {metric.calculationDetails && (
                    <div className="mb-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Calculation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {metric.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelConsumption;